import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useDeepCompareEffect } from "react-use";

import {
  updateCompanyDetails,
  UpdateCompanyDetailsPayload,
} from "@/account/api/company";
import { Button } from "@/design-system/components/Button/Button";
import { Input } from "@/design-system/components/Input/Input";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import { unpackRequestErrorMessage } from "@/utils/request";
import { AccountMetadata } from "@/reducers/environments";

function initialValues(
  accountMetadata: AccountMetadata,
): UpdateCompanyDetailsPayload {
  return {
    newName: accountMetadata.companyDetails.name,
  };
}

export function UpdateCompanyInfoForm() {
  const accountMetadata = useAccountMetadata();
  const { register, handleSubmit, formState, setError, reset } =
    useForm<UpdateCompanyDetailsPayload>({
      defaultValues: initialValues(accountMetadata),
    });

  useDeepCompareEffect(() => {
    reset(initialValues(accountMetadata));
  }, [reset, accountMetadata]);

  const onSubmit = useCallback(
    async (values: UpdateCompanyDetailsPayload) => {
      try {
        await updateCompanyDetails(values);
      } catch (error) {
        setError("root", { message: unpackRequestErrorMessage(error) });
      }
    },
    [setError],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Company name"
        {...register("newName", { required: "Company name is required" })}
        error={formState.errors.newName}
      />

      <div className="flex justify-end gap-4 pt-4">
        {formState.errors.root && (
          <p className="text-textError text-sm">
            {formState.errors.root.message}
          </p>
        )}
        <Button
          type="submit"
          disabled={
            formState.isSubmitting || !formState.isValid || !formState.isDirty
          }
          leadingIcon={
            formState.isSubmitting ? (
              <LoadingSpinner className="size-4" />
            ) : undefined
          }
        >
          Save
        </Button>
      </div>
    </form>
  );
}
